import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import { next } from '@ember/runloop';
import { htmlSafe, type SafeString } from '@ember/template';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import optional from '@nullvoxpopuli/ember-composable-helpers/helpers/optional';
import { t } from 'ember-intl';
import onKey from 'ember-keyboard/modifiers/on-key';
import { ref } from 'ember-ref-bucket';
import createRef from 'ember-ref-bucket/modifiers/create-ref';
import { and, eq, not, or } from 'ember-truth-helpers';
import BvConnected from 'volta/components/bv-connected';
import BvLabelled from 'volta/components/bv-labelled';
import BvTextFieldSpinner from 'volta/components/bv-text-field/spinner';
import classNames from 'volta/helpers/class-names';
import stopPropagation from 'volta/helpers/stop-propagation';
import autoresize from 'volta/modifiers/autoresize';
import { normalizeAutoComplete } from 'volta/utils/dom-utils';
import { parseNumber, round } from 'volta/utils/math-utils';
import BvIcon from 'volta/components/bv-icon';

import type { ComponentLike, ContentValue } from '@glint/template';
import type { BvLabelledSignature } from './../bv-labelled';

/**
 * Returns the length of decimal places in a number
 *
 * @param {number} num The number to test
 * @returns {number} The length of decimal places in a number
 */
const dpl = (num: number) => (num.toString().split('.')[1] ?? []).length;

type TInputType =
  | 'text'
  | 'email'
  | 'number'
  | 'password'
  | 'search'
  | 'tel'
  | 'url'
  | 'date'
  | 'datetime-local'
  | 'month'
  | 'time'
  | 'week'
  | 'currency'
  | 'percent';

type TNumericInputType = 'number' | 'percent';
type TTextInputType = Exclude<TInputType, TNumericInputType>;
type TBivariantCallback<Args extends unknown[], Return = void> = {
  bivarianceHack(...args: Args): Return;
}['bivarianceHack'];
type TTextFieldOnChangeString = TBivariantCallback<
  [value: string, id: string, event?: InputEvent]
>;
type TTextFieldOnChangeNumber = TBivariantCallback<
  [value: number, id: string, event?: InputEvent]
>;

type BvTextFieldSharedArgs = Pick<
  BvLabelledSignature['Args'],
  | 'label'
  | 'error'
  | 'warning'
  | 'helpText'
  | 'isRequired'
  | 'inline'
  | 'inlineForm'
  | 'class'
  | 'labelWrapperClass'
  | 'isEdited'
  | 'fullWidth'
> & {
  /**
   * ID for the input
   */
  id?: string;
  /**
   * Text to display before value
   */
  prefix?: ContentValue | ComponentLike;
  /**
   * Text to display after value
   */
  suffix?: ContentValue | ComponentLike;
  /**
   * Hint text to display
   */
  placeholder?: string | SafeString;
  /**
   * Initial value for the input
   */
  value?: string | number | null;

  /**
   * Adds an action to the label
   *
   * Currently supports:
   * { onClick, text, accessibilityLabel }
   */
  labelAction?: { onClick: () => void; text: string; accessibilityLabel?: string };
  /**
   * Visually hide the label
   */
  labelHidden?: boolean;
  /**
   * Disable the input
   */
  disabled?: boolean;
  /**
   * Disable editing of the input
   */
  readOnly?: boolean;
  /**
   * Automatically focus the input
   */
  autoFocus?: boolean;
  /**
   * Force the focus state on the input
   */
  focused?: boolean;
  /**
   * Allow for multiple lines of input
   */
  multiline?: boolean | number;
  /**
   * Maximum textarea height (in px) for multiline input
   */
  maxHeight?: number;

  /**
   * An element connected to the right of the input
   */
  connectedRight?: ComponentLike;
  /**
   * An element connected to the left of the input
   */
  connectedLeft?: ComponentLike;
  /**
   * Name of the input
   */
  name?: string;
  /**
   * Defines a specific role attribute for the input
   */
  role?: string;
  /**
   * Limit increment value for numeric and date-time inputs
   */
  step?: number;
  /**
   * Enable automatic completion by the browser
   */
  autoComplete?: 'on' | 'off' | string | boolean | null;
  /**
   * Mimics the behavior of the native HTML attribute,
   * limiting how high the spinner can increment the value
   */
  max?: number;
  /**
   * Maximum character length for an input
   */
  maxLength?: number;
  /**
   * Mimics the behavior of the native HTML attribute,
   * limiting how low the spinner can decrement the value
   */
  min?: number;
  /**
   * Minimum character length for an input
   */
  minLength?: number;
  /**
   * A regular expression to check the value against
   */
  pattern?: string;
  /**
   * Indicate whether value should have spelling checked
   */
  spellCheck?: `${boolean}`;
  /**
   * Indicates the id of a component owned by the input
   */
  ariaOwns?: string;
  /**
   * Indicates the id of a component controlled by the input
   */
  ariaControls?: string;
  /**
   * Indicates the id of a related component's visually focused element ot the input
   */
  ariaActiveDescendant?: string;
  /**
   * Indicates what kind of user input completion suggestions are provided
   */
  ariaAutocomplete?: string;

  /**
   * Height of the textarea (in px) for multiline input
   */
  height?: number;

  /**
   * Additional information to display in the label
   */
  labelInfo?: ContentValue | null;

  /**
   * When true, the input will have no padding and will not grow in height when multiline is enabled.
   * This is useful when you want to control the height of the input with CSS or when using it in a fixed height container.
   */
  plain?: boolean;

  /**
   * When true, the input will use a monospace font and have a different background color to indicate code input.
   */
  code?: boolean;

  /**
   * When true, the textarea will not automatically resize based on content.
   * This is useful when you want to control the height of the textarea with CSS or when using it in a fixed height container.
   */
  disableAutoresize?: boolean;

  ariaInvalid?: boolean;

  /**
   * When true, a button will be displayed to clear the input when it has a value.
   */
  clearButton?: boolean;
  /**
   * Callback when key is pressed
   */
  onKeyPress?: (event: KeyboardEvent) => void;
  /**
   * Callback when Enter is pressed
   */
  onEnter?: (value: string, id: string, event: KeyboardEvent) => void;
  /**
   * Callback when Escape key is triggered
   */
  onEscape?: (event: KeyboardEvent, value: string, id: string) => void;
  /**
   * Callback when input is focused
   */
  onFocus?: (e: FocusEvent) => void;
  /**
   * Callback when focus is removed
   */
  onBlur?: (e: FocusEvent) => void;
  onClearButtonClick?: (id: string, event: MouseEvent) => void;
  onIncrease?: () => void;
  onDecrease?: () => void;
};

type BvTextFieldValueArgs =
  | {
      /**
       * Determine type of input
       *
       * @default text
       */
      type?: TTextInputType;
      /**
       * Indicates whether we should return the value as a number
       */
      parseToFloat?: boolean;
      /**
       * Callback when value is changed
       */
      onChange?: TTextFieldOnChangeString;
    }
  | {
      /**
       * Determine type of input
       */
      type: TNumericInputType;
      /**
       * Indicates whether we should return the value as a number
       */
      parseToFloat?: true;
      /**
       * Callback when value is changed
       */
      onChange?: TTextFieldOnChangeNumber;
    }
  | {
      /**
       * Determine type of input
       */
      type: TNumericInputType;
      /**
       * Indicates whether we should return the value as a number
       */
      parseToFloat?: false;
      /**
       * Callback when value is changed
       */
      onChange?: TTextFieldOnChangeString;
    };

export interface BvTextFieldSignature {
  Args: BvTextFieldSharedArgs & BvTextFieldValueArgs;
  Blocks: {
    fieldActions: [];
  };
  Element: HTMLElement;
}

export default class BvTextFieldComponent extends Component<BvTextFieldSignature> {
  @tracked value?: number | string;
  // eslint-disable-next-line ember/no-tracked-properties-from-args
  @tracked focus = this.args.focused;

  dataTestTextField = 'bv-text-field';
  id: string = this.args.id ?? `BvTextField-${guidFor(this)}`;
  @ref('input') input?: HTMLElement;

  constructor(owner: any, args: BvTextFieldSignature['Args']) {
    super(owner, args);
    this.initValue();
  }

  initValue() {
    const { type, percentRatio, inputType } = this;
    const { value } = this.args;
    const newRawInputValue = isPresent(value)
      ? `${type === 'percent' ? round((parseNumber(value) as number) / percentRatio, 6) : value}`
      : '';
    const hasRawValueChanged =
      inputType === 'number'
        ? parseNumber(this.value) !== parseNumber(newRawInputValue)
        : this.value !== newRawInputValue;

    if (hasRawValueChanged) {
      // eslint-disable-next-line ember/no-runloop
      next(() => {
        this.value = newRawInputValue;
      });
    }
  }

  get type() {
    return this.args.type ?? 'text';
  }

  /**
   * Value as string or empty string
   */
  get normalizedValue() {
    return isPresent(this.value) ? `${this.value}` : '';
  }

  get ariaInvalid() {
    return Boolean(this.args.error);
  }

  get autoCompleteInputs() {
    return normalizeAutoComplete(this.args.autoComplete);
  }

  get ariaDescribedBy() {
    const { error, helpText } = this.args;
    const { id } = this;
    const describedBy = [];

    if (error) {
      describedBy.push(`${id}Error`);
    }

    if (helpText) {
      describedBy.push(`${id}HelpText`);
    }

    return describedBy.join(' ');
  }

  get ariaLabelledBy() {
    const { prefix, suffix } = this.args;
    const { id } = this;
    const labelledBy = [`${id}Label`];

    if (prefix) {
      labelledBy.push(`${id}Prefix`);
    }

    if (suffix) {
      labelledBy.push(`${id}Suffix`);
    }

    return labelledBy.join(' ');
  }

  get inputType() {
    const { type } = this;
    switch (type) {
      case 'currency': {
        return 'text';
      }
      case 'percent': {
        return 'number';
      }
      default: {
        return type;
      }
    }
  }

  get minimumLines(): number {
    const { multiline } = this.args;
    return typeof multiline === 'number' ? multiline : 1;
  }

  get heightStyle() {
    if (!this.args.multiline || this.args.plain) {
      return null;
    }

    const { maxHeight, height } = this.args;
    const hasMaxHeight = typeof maxHeight === 'number' && maxHeight > 0;
    const actualHeight = hasMaxHeight ? Math.min(height ?? 0, maxHeight) : height;
    const styles = [];

    if (actualHeight) {
      styles.push(`height: ${actualHeight}px`);
    }

    if (hasMaxHeight) {
      styles.push(`max-height: ${maxHeight}px`);
    }

    return styles.length ? htmlSafe(styles.join(';')) : null;
  }

  get shouldShowSpinner() {
    return this.inputType === 'number' && !this.args.disabled && !this.args.readOnly;
  }

  get percentRatio() {
    return this.type === 'percent' ? 0.01 : 1;
  }

  checkFocus() {
    // To avoid focus and conflicts when rendering
    if (this.input && this.args.focused) {
      this.focus = true;
      // eslint-disable-next-line ember/no-runloop
      next(() => {
        this.input?.focus();
      });
    }
  }

  @action
  handleFocusChange(/*element, [focused]*/) {
    this.checkFocus();
  }

  @action
  handleValueChange() {
    this.initValue();
  }

  @action
  handleDidInsert(/*element*/) {
    this.checkFocus();
  }

  @action
  handleNumberChange(steps: number) {
    let { step, min, max } = this.args;

    step = step ?? 1;
    min = (
      isPresent(min) ? (min as number) / this.percentRatio : Number.NEGATIVE_INFINITY
    ) as number;
    max = (
      isPresent(max) ? (max as number) / this.percentRatio : Number.POSITIVE_INFINITY
    ) as number;

    const numericValue = this.value ? parseNumber(this.value) : min;

    if (numericValue === undefined) {
      return;
    }

    // Making sure the new value has the same length of decimal places as the
    // step / value has.
    const infinityAdjusted = numericValue + steps * step;
    const decimalPlaces = Math.max(dpl(numericValue), dpl(step));
    const newValue = Math.min(
      max,
      Math.max(infinityAdjusted === Number.NEGATIVE_INFINITY ? steps * step : infinityAdjusted, min)
    );
    this.value = newValue.toFixed(decimalPlaces);

    if (this.args.type !== 'number' && this.args.type !== 'percent') {
      return;
    }

    if (this.args.parseToFloat === false) {
      (this.args.onChange as TTextFieldOnChangeString | undefined)?.(
        String((newValue * this.percentRatio).toFixed(decimalPlaces)),
        this.id
      );
    } else {
      (this.args.onChange as TTextFieldOnChangeNumber | undefined)?.(
        newValue * this.percentRatio,
        this.id
      );
    }
  }

  @action
  handleChange(event: Event) {
    if (!event) {
      return;
    }
    const { value, valueAsNumber } = event.currentTarget as HTMLInputElement;

    this.value = value;

    if (this.args.type === 'number' || this.args.type === 'percent') {
      if (this.args.parseToFloat === false) {
        (this.args.onChange as TTextFieldOnChangeString | undefined)?.(
          value,
          this.id,
          event as InputEvent
        );
      } else {
        (this.args.onChange as TTextFieldOnChangeNumber | undefined)?.(
          round(valueAsNumber * this.percentRatio, 6) as number,
          this.id,
          event as InputEvent
        );
      }
      return;
    }

    (this.args.onChange as TTextFieldOnChangeString | undefined)?.(
      value,
      this.id,
      event as InputEvent
    );
  }

  @action
  handleInputFocus(event: FocusEvent) {
    if (this.inputType === 'number') {
      this.input?.addEventListener('wheel', (e: WheelEvent) => {
        e.preventDefault();
      });
    }
    this.args.onFocus?.(event);
  }

  @action
  handleInputBlur(event: FocusEvent) {
    if (this.inputType === 'number') {
      this.input?.removeEventListener('wheel', (e: WheelEvent) => {
        e.preventDefault();
      });
    }
    this.args.onBlur?.(event);
  }

  @action
  handleFocus() {
    this.focus = true;
  }

  @action
  handleBlur() {
    this.focus = false;
  }

  @action
  handleClick() {
    this.input?.focus();
  }

  @action
  handleEscape(event: KeyboardEvent) {
    const { target } = event;

    return this.args.onEscape?.(event, (target as HTMLInputElement).value, this.id);
  }

  @action
  handleEnter(event: KeyboardEvent) {
    const { onEnter, multiline } = this.args;
    if (onEnter && ((event.metaKey && multiline) || !multiline)) {
      event.preventDefault();
      return onEnter((event.target as HTMLInputElement).value, this.id, event);
    }
  }

  @action
  handleClearButtonPress(event: Event) {
    if (this.args.disabled) {
      return;
    }
    this.args.onClearButtonClick?.(this.id, event as MouseEvent);
  }

  <template>
    <BvLabelled
      class={{@class}}
      @dataTestLabelled={{this.dataTestTextField}}
      @isRequired={{@isRequired}}
      @id={{this.id}}
      @label={{@label}}
      @error={{@error}}
      @warning={{@warning}}
      @action={{@labelAction}}
      @labelHidden={{@labelHidden}}
      @helpText={{@helpText}}
      @inline={{@inline}}
      @inlineForm={{@inlineForm}}
      @info={{@labelInfo}}
      @isEdited={{@isEdited}}
      @fullWidth={{@fullWidth}}
      @labelWrapperClass={{@labelWrapperClass}}
      data-test-text-field-label={{@name}}
      ...attributes
    >
      <BvConnected @left={{@connectedLeft}} @right={{@connectedRight}}>
        {{! template-lint-disable no-invalid-interactive }}
        {{! template-lint-disable no-at-ember-render-modifiers }}
        <div
          data-test-text-field-wrapper
          class={{classNames
            'bv-text-field'
            (if @disabled 'bv-text-field--disabled')
            (if @readOnly 'bv-text-field--read-only')
            (if @error 'bv-text-field--error')
            (if @warning 'bv-text-field--warning')
            (if (and (not @plain) @multiline) 'bv-text-field--multiline')
            (if @code 'bv-text-field--code')
            (if this.focus 'bv-text-field--focus')
            (if @inline 'u-d-inline-flex')
          }}
          {{on 'focus' this.handleFocus}}
          {{on 'blur' this.handleBlur}}
          {{on 'click' this.handleClick}}
          {{didInsert this.handleDidInsert}}
          {{didUpdate this.handleFocusChange @focused}}
          {{didUpdate this.handleValueChange @value @type}}
        >
          {{#if @prefix}}
            <div data-test-text-field-prefix class='bv-text-field__prefix' id='{{this.id}}Prefix'>
              {{! @glint-ignore: this is fine }}
              {{@prefix}}
            </div>
          {{/if}}
          {{#if @multiline}}
            {{! template-lint-disable no-duplicate-id }}
            {{! template-lint-disable require-input-label }}
            {{! template-lint-disable no-autofocus-attribute }}
            <textarea
              data-test-text-field-input='true'
              data-test-text-field-name={{@name}}
              name={{@name}}
              id={{this.id}}
              disabled={{@disabled}}
              readonly={{@readOnly}}
              role={{@role}}
              autofocus={{@autoFocus}}
              placeholder={{@placeholder}}
              autocomplete={{this.autoCompleteInputs}}
              max={{@max}}
              min={{@min}}
              minlength={{@minLength}}
              maxlength={{@maxLength}}
              spellcheck={{@spellCheck}}
              value={{this.normalizedValue}}
              class={{if
                @plain
                (classNames
                  'bv-text-field__textarea--plain'
                  (unless @disableAutoresize 'bv-text-field__textarea--plain--auto-resize')
                )
                (classNames 'bv-text-field__input' (if @suffix 'bv-text-field__input--suffixed'))
              }}
              aria-multiline='true'
              style={{this.heightStyle}}
              aria-describedby={{this.ariaDescribedBy}}
              aria-label={{@label}}
              aria-labelledby={{this.ariaLabelledBy}}
              aria-invalid={{@ariaInvalid}}
              aria-owns={{@ariaOwns}}
              aria-activedescendant={{@ariaActiveDescendant}}
              aria-autocomplete={{@ariaAutocomplete}}
              aria-controls={{@ariaControls}}
              {{autoresize this.normalizedValue (or @disableAutoresize @plain) mode='height'}}
              {{on 'input' this.handleChange}}
              {{on 'focus' (optional @onFocus)}}
              {{on 'blur' (optional @onBlur)}}
              {{onKey 'Enter' this.handleEnter}}
              {{onKey 'cmd+Enter' this.handleEnter}}
              {{onKey 'Escape' this.handleEscape}}
              {{createRef 'input' bucket=this}}
            ></textarea>
          {{else}}
            {{! template-lint-disable no-autofocus-attribute }}
            {{! template-lint-disable require-input-label }}
            <input
              data-test-text-field-input='true'
              data-test-text-field-name={{@name}}
              name={{@name}}
              id={{this.id}}
              disabled={{@disabled}}
              readonly={{@readOnly}}
              role={{@role}}
              autofocus={{@autoFocus}}
              placeholder={{@placeholder}}
              autocomplete={{this.autoCompleteInputs}}
              max={{@max}}
              min={{@min}}
              minlength={{@minLength}}
              maxlength={{@maxLength}}
              spellcheck={{@spellCheck}}
              value={{this.normalizedValue}}
              class={{classNames
                'bv-text-field__input'
                (if @suffix 'bv-text-field__input--suffixed')
                (if @clearButton 'bv-text-field__input--has-clear-btn')
                (if (eq @type 'percent') 'bv-text-field__input--percent')
              }}
              aria-describedby={{this.ariaDescribedBy}}
              aria-label={{@label}}
              aria-labelledby={{this.ariaLabelledBy}}
              aria-invalid={{@ariaInvalid}}
              aria-owns={{@ariaOwns}}
              aria-activedescendant={{@ariaActiveDescendant}}
              aria-autocomplete={{@ariaAutocomplete}}
              aria-controls={{@ariaControls}}
              type={{this.inputType}}
              {{on 'input' this.handleChange}}
              {{on 'focus' this.handleInputFocus}}
              {{on 'blur' this.handleInputBlur}}
              {{onKey 'Enter' this.handleEnter}}
              {{onKey 'Escape' this.handleEscape}}
              {{createRef 'input' bucket=this}}
            />
          {{/if}}
          {{#if @suffix}}
            <div data-test-text-field-suffix class='bv-text-field__suffix' id='{{this.id}}Suffix'>
              {{! @glint-ignore: this is fine }}
              {{@suffix}}
            </div>
          {{/if}}
          {{#if (and @clearButton (isPresent @value))}}
            <button
              type='button'
              class='bv-text-field__clear'
              disabled={{@disabled}}
              {{on 'click' (stopPropagation this.handleClearButtonPress)}}
            >
              <span class='u-visually-hidden'>
                {{t 'clear'}}
              </span>
              <BvIcon @glyph='close-circle' />
            </button>
          {{/if}}
          {{#if this.shouldShowSpinner}}
            <BvTextFieldSpinner
              @onChange={{this.handleNumberChange}}
              @onIncrease={{@onIncrease}}
              @onDecrease={{@onDecrease}}
            />
          {{/if}}
          {{#unless @plain}}
            <div class='bv-text-field__backdrop'></div>
          {{/unless}}
          {{#if (has-block 'fieldActions')}}
            {{yield to='fieldActions'}}
          {{/if}}
        </div>
      </BvConnected>
    </BvLabelled>
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvTextField: typeof BvTextFieldComponent;
    'bv-text-field': typeof BvTextFieldComponent;
  }
}
